import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { inject, Component, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ModulesService } from '@shared/services/modules.service';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MaterialModule } from '@app/material.module';
import { LoggerService } from '@app/logger/logger.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';

import { RemissionsService } from '@app/shared/services/entities/remissions.service';
import { DoctorsService } from '@app/shared/services/entities/doctors.service';
import { PlacesService } from '@app/shared/services/entities/places.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { ContractsService } from '@app/shared/services/entities/contracts.service';
import { InterventionsService } from '@app/shared/services/entities/interventions.service';
import { UsersService } from '@app/shared/services/entities/users.service';
import { RolesService } from '@app/shared/services/entities/roles.service';
import { ActivatedRoute } from '@angular/router';

const log = new LoggerService('REMISSIONS COMPONENT');

/**
 * Remisiones — documento comercial de material entregado con cargo.
 *
 * Dos orígenes: las que nacen del asistente de intervención (paso 4, lo
 * fuera de paquete) y las DIRECTAS que se capturan aquí sin cirugía de por
 * medio (reposición, venta directa — proceso "Salida de material remisión"
 * del forms de campo). El folio (REM-n) lo asigna el servidor.
 */
@Component({
  standalone: true,
  imports: [PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, FormsModule, ReactiveFormsModule, MaterialModule, DecimalPipe],
  selector: 'app-remissions',
  templateUrl: './remissions.component.html',
  styleUrls: ['./remissions.component.scss'],
})
export class RemissionsComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/remissions', 'Remisiones');

  public remissionsList: any[] = [];
  public dataSource = new MatTableDataSource<any>([]);
  public columns = ['folio', 'createdOn', 'doctorName', 'place', 'lines', 'total', 'status', 'actions'];

  public doctorsList: any[] = [];
  public placesList: any[] = [];
  public productsList: any[] = [];
  public contract: any = null;

  public panelOpen = false;
  public form!: FormGroup;
  public interventionsList: any[] = [];
  // Línea estilo Medallia: hasta dos pares cantidad/lote por producto.
  public lines: any[] = [];
  public productFilter = '';

  // Técnico Medtronic (pantalla 2 del forms de remisión)
  public technicianList: any[] = [];
  public technicianLocked = false;
  public technicianLockedName = '';

  // Otros — renglones fuera de catálogo (CFN / piezas / lote)
  public otherItems: { cfn: string | null; pieces: number | null; batch: string | null }[] = [];
  public otherAdd: any = { cfn: null, pieces: 1, batch: null };

  constructor(
    private formBuilder: FormBuilder,
    private customMessageService: CustomMessageService,
    private remissionsService: RemissionsService,
    private doctorsService: DoctorsService,
    private placesService: PlacesService,
    private productsService: ProductsService,
    private contractsService: ContractsService,
    private interventionsService: InterventionsService,
    private activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    private rolesService: RolesService,
  ) {}

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['remissions', 'interventions'], () => this.pageLoad.ready && this.pageLoad.refresh(this.reload()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    this.form = this.formBuilder.group({
      technician: [null, Validators.required],
      place: [null, Validators.required],
      doctor: [null],
      // Folio de cirugía (opcional): una remisión puede nacer ligada a una
      // intervención o completamente directa.
      intervention: [null],
      notes: [null],
    });
    // Si falla, la pantalla de carga muestra el error con "Reintentar".
    await Promise.all([this.loadRemissions(), this.loadCatalogs(), this.loadTechnicians()]);

    // Llegada desde Salida de material ("Nueva remisión"): abrir la captura.
    const create = this.activatedRoute.snapshot.queryParamMap.get('create');
    if (create === '1') this.openCreate();
  }

  /** Botón Actualizar de la barra de acciones. */
  /** Periodo de la lista: por omisión el mes actual; se amplía con el filtro. */
  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();
  get periodChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.periodStart || this.periodEnd) {
      const f = (d: Date | null) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '…';
      c.push({ key: 'period', label: 'Periodo', value: f(this.periodStart) + ' – ' + f(this.periodEnd), removable: false });
    }
    return c;
  }

  /** Búsqueda de texto sobre la lista de remisiones. */
  applySearch(value: string): void {
    this.dataSource.filter = (value ?? '').trim().toLowerCase();
  }

  async reload(): Promise<void> { await this.loadRemissions(); }

  private async loadRemissions(): Promise<void> {
    const response = await this.remissionsService.search({ searchType: 'default', condition: { deleted: false } });
    this.remissionsList = ((response.data as any[]) ?? [])
      .filter(x => x)
      .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
    this.dataSource.data = this.remissionsList.filter(r => inDateRange(r.createdOn, this.periodStart, this.periodEnd));
  }

  private async loadCatalogs(): Promise<void> {
    const [doctors, places, products, contracts, interventions] = await Promise.all([
      this.doctorsService.search({ searchType: 'default', condition: { deleted: false } }),
      this.placesService.search({ searchType: 'default', condition: { deleted: false } }),
      this.productsService.search({ searchType: 'default', condition: { deleted: false } }),
      this.contractsService.search({ searchType: 'default', condition: { deleted: false } }),
      // Folios en curso, para poder ligar la remisión a una cirugía.
      this.interventionsService.search({ searchType: 'default', condition: { deleted: false, status: ['Pendiente', 'Permiso', 'Borrador', 'Incompleto'] } }),
    ]);
    this.doctorsList = ((doctors.data as any[]) ?? []).filter(x => x);
    this.placesList = ((places.data as any[]) ?? []).filter(x => x);
    this.productsList = ((products.data as any[]) ?? []).filter(x => x)
      .sort((a, b) => String(a.code).localeCompare(String(b.code), 'es'));
    this.contract = ((contracts.data as any[]) ?? []).find(c => c && (c.status === true || c.status === 'true')) ?? null;
    this.interventionsList = ((interventions.data as any[]) ?? []).filter(x => x?.fileNumber)
      .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
  }

  /**
   * Usuarios con rol Técnico para el selector radial. Si el usuario actual
   * ES técnico, queda preseleccionado y fijo.
   */
  private async loadTechnicians(): Promise<void> {
    const normalize = (name: any) =>
      String(name ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const [rolesResponse, usersResponse, me] = await Promise.all([
      this.rolesService.search({ searchType: 'default', condition: { deleted: false } }),
      this.usersService.search({ searchType: 'default', condition: { deleted: false } }),
      this.usersService.loadCurrentUser(),
    ]);
    const techRoleIds = new Set(
      ((rolesResponse.data as any[]) ?? []).filter(r => r && normalize(r.name) === 'tecnico').map(r => r.id),
    );
    this.technicianList = ((usersResponse.data as any[]) ?? [])
      .filter(u => u && u.role && techRoleIds.has(u.role) && u.active !== false)
      .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), 'es'));
    if (me && me.role && techRoleIds.has(me.role)) {
      this.technicianLocked = true;
      this.technicianLockedName = me.name ?? '';
      if (!this.technicianList.some(u => u.id === me.id)) this.technicianList.push(me);
      this.form.get('technician')?.setValue(me.id);
      this.form.get('technician')?.disable();
    }
  }

  /**
   * Al ligar un folio de cirugía existente se jalan sus datos principales:
   * hospital, médico y técnico (lo demás ya vive en el folio).
   */
  onInterventionChange(): void {
    const interventionId = this.form.get('intervention')?.value;
    const record = this.interventionsList.find(i => i.id === interventionId);
    if (!record) return;
    if (record.hospital) this.form.get('place')?.setValue(record.hospital);
    if (record.doctor && this.doctorsList.some(d => d.id === record.doctor)) {
      this.form.get('doctor')?.setValue(record.doctor);
    }
    if (record.technician && !this.technicianLocked
        && this.technicianList.some(t => t.id === record.technician)) {
      this.form.get('technician')?.setValue(record.technician);
    }
  }

  // ─── Otros (CFN / piezas / lote) ─────────────────────────────────────────

  addOther(): void {
    if (!this.otherAdd.cfn || !this.otherAdd.pieces || this.otherAdd.pieces < 1) return;
    this.otherItems.push({ cfn: String(this.otherAdd.cfn).trim().toUpperCase(), pieces: Number(this.otherAdd.pieces), batch: this.otherAdd.batch || null });
    this.otherAdd = { cfn: null, pieces: 1, batch: null };
  }

  removeOther(index: number): void {
    this.otherItems.splice(index, 1);
  }

  // ─── Helpers de la tabla ─────────────────────────────────────────────────

  placeName(id: string): string {
    return this.placesList.find(p => p.id === id)?.name ?? '—';
  }

  formatDate(value: string): string {
    if (!value) return '—';
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // ─── Captura directa ─────────────────────────────────────────────────────

  openCreate(): void {
    this.panelOpen = true;
    this.lines = [];
    this.otherItems = [];
    this.otherAdd = { cfn: null, pieces: 1, batch: null };
    this.productFilter = '';
    this.form.reset();
    // El técnico fijo (usuario actual con rol Técnico) sobrevive al reset.
    if (this.technicianLocked) {
      const me = this.usersService.currentUser();
      if (me) this.form.get('technician')?.setValue(me.id);
    }
  }

  closePanel(): void {
    this.panelOpen = false;
  }

  public get filteredProducts(): any[] {
    const f = this.productFilter.trim().toLowerCase();
    if (!f) return [];
    return this.productsList
      .filter(p => String(p.code ?? '').toLowerCase().includes(f) || String(p.description ?? '').toLowerCase().includes(f))
      .slice(0, 30);
  }

  /** Precio del contrato por partida (itemNumber) o descripción. */
  private priceFor(product: any): number | null {
    if (!this.contract?.prices || !product) return null;
    const row = (this.contract.prices as any[]).find(pr =>
      (product.itemNumber && String(pr.itemNumber) === String(product.itemNumber)) ||
      (pr.description && product.description && String(pr.description).trim().toUpperCase() === String(product.description).trim().toUpperCase()));
    const price = row ? Number(row.price) : NaN;
    return isNaN(price) ? null : price;
  }

  addLine(product: any): void {
    const price = this.priceFor(product);
    // Formato Medallia: hasta dos pares cantidad/lote por renglón.
    this.lines.push({
      product: product.id,
      code: product.code,
      description: product.description,
      qty1: 1,
      batch1: null,
      qty2: null,
      batch2: null,
      unitPrice: price ?? 0,
      noCharge: false,
      reason: null,
      sinPrecio: price === null,
    });
    this.productFilter = '';
  }

  public lineQuantity(line: any): number {
    return Math.max(0, Number(line.qty1) || 0) + Math.max(0, Number(line.qty2) || 0);
  }

  removeLine(index: number): void {
    this.lines.splice(index, 1);
  }

  public get linesWithoutPrice(): boolean {
    return this.lines.some(l => l.sinPrecio && !l.noCharge);
  }

  public get total(): number {
    return this.lines.reduce((sum, l) => sum + (l.noCharge ? 0 : (Number(l.unitPrice) || 0) * this.lineQuantity(l)), 0);
  }

  private async persist(status: 'Borrador' | 'Emitida'): Promise<void> {
    if (this.form.invalid) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Elige el técnico Medtronic y el hospital/cliente.', dismissBtn: true });
      return;
    }
    if (this.lines.length === 0 && this.otherItems.length === 0) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Agrega al menos una línea de material (o un renglón en Otros).', dismissBtn: true });
      return;
    }
    if (this.lines.some(l => this.lineQuantity(l) < 1)) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Hay líneas sin cantidad: captura cantidad 1 (y opcionalmente cantidad 2).', dismissBtn: true });
      return;
    }
    // getRawValue: el control de técnico puede estar deshabilitado (usuario técnico fijo).
    const formValue: any = this.form.getRawValue();
    const doctor = this.doctorsList.find(d => d.id === formValue.doctor);
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Guardando remisión...', loading: true });
    try {
      // Cada par cantidad/lote se expande a su propia partida, para que los
      // reportes y el total por partida sigan funcionando igual.
      const items = this.lines.flatMap(({ sinPrecio, qty1, batch1, qty2, batch2, ...line }) => {
        const out: any[] = [];
        if (Number(qty1) > 0) out.push({ ...line, quantity: Number(qty1), batch: batch1 || null });
        if (Number(qty2) > 0) out.push({ ...line, quantity: Number(qty2), batch: batch2 || null });
        return out;
      });
      const response: any = await this.remissionsService.create({
        place: formValue.place,
        contract: this.contract?.id ?? null,
        doctorName: doctor?.name ?? null,
        technician: formValue.technician ?? null,
        intervention: formValue.intervention ?? null,
        notes: formValue.notes,
        status,
        items,
        otherItems: this.otherItems,
      });
      this.customMessageService.close();
      if (response.state === 'success') {
        const folio = response.data?.[0]?.folio ?? '';
        await this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: `Remisión <b>${folio}</b> ${status === 'Emitida' ? 'emitida' : 'guardada como borrador'}.`, duration: 2200 });
        this.panelOpen = false;
        await this.loadRemissions();
      }
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  saveDraft(): void { void this.persist('Borrador'); }
  saveAndEmit(): void { void this.persist('Emitida'); }

  async changeStatus(remission: any, status: string): Promise<void> {
    const confirmed = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Cambiar la remisión <b>${remission.folio}</b> a <b>${status}</b>?`,
      dismissBtn: true, acceptBtn: true,
    });
    if (!confirmed) return;
    try {
      await this.remissionsService.update({ id: remission.id, status });
      await this.loadRemissions();
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }
}
